import { useCallback, useEffect, useRef } from 'react';

import { reaction, when } from 'mobx';

import { usePhoneVerifyDialog } from '../../../../hooks/usePhoneVerifyDialog';
import type { Profile } from '../../../../services/auth';
import { useFlag } from '../../../../services/launchDarkly';
import type { CoinType } from '../../../../stores/cointype/CoinTypeStore';
import { useStores } from '../../../../stores/useStores';

type PhoneProfile = Pick<Profile, 'phone' | 'phoneVerified'> | null | undefined;

/** Gate on the coin the game will be launched with (the CTA target), not the lobby's current coin. */
export const needsPhoneVerification = (
  coin: CoinType | undefined,
  isPhoneVerificationRequired: boolean,
  profile: PhoneProfile,
) => {
  // Same "verified" expression as GameCard.
  const phoneVerified = !isPhoneVerificationRequired || (profile?.phone && profile?.phoneVerified);
  return coin === 'SC' && !phoneVerified;
};

type PendingVerification = Promise<void> & { cancel(): void };

/**
 * Runs `start` immediately, or — for an SC launch by a player without a verified phone — only after the
 * phone verification wizard succeeds. Closing the wizard without verifying drops the launch.
 */
export const useBannerPhoneGate = () => {
  const { userStore, portalStore } = useStores();
  const { openVerifyPhoneDialog } = usePhoneVerifyDialog();
  const isPhoneVerificationRequired = useFlag('phone_verification_dialog', true);
  const pendingRef = useRef<PendingVerification | null>(null);

  useEffect(
    () => () => {
      pendingRef.current?.cancel();
      pendingRef.current = null;
    },
    [],
  );

  return useCallback(
    (coin: CoinType | undefined, start: () => void) => {
      if (!needsPhoneVerification(coin, isPhoneVerificationRequired, userStore.profile)) {
        start();
        return;
      }

      pendingRef.current?.cancel();
      const verified = when(() => !needsPhoneVerification(coin, isPhoneVerificationRequired, userStore.profile));
      pendingRef.current = verified;

      const clearPending = () => {
        if (pendingRef.current === verified) pendingRef.current = null;
      };

      verified.then(
        () => {
          clearPending();
          start();
        },
        // Rejects on cancel (wizard closed without verifying, superseded click, unmount): no launch.
        clearPending,
      );

      let success = false;
      openVerifyPhoneDialog(
        () => {
          success = true;
        },
        {
          beforeClose: () => {
            if (!success) verified.cancel();
          },
        },
      );

      // The wizard can go away without our beforeClose: replaced by another forced portal, or closed with
      // preventBeforeClose. Any hide of the portal before success drops the launch. Set up after opening so the
      // wizard's own open (which hides a currently visible portal first) doesn't trip it.
      const disposePortalWatch = reaction(
        () => portalStore.isVisible,
        (isVisible) => {
          if (!isVisible && !success) verified.cancel();
        },
      );
      void verified.finally(disposePortalWatch).catch(() => undefined);

      // openPortal is a no-op during the cool-down dialog — nothing to wait for.
      if (!portalStore.isVisible && !portalStore.isRefreshing) verified.cancel();
    },
    [isPhoneVerificationRequired, openVerifyPhoneDialog, portalStore, userStore],
  );
};
